import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useToast } from '../context/ToastContext';
import { formatDateTime } from '../utils/formatDate';
import { useClientPagination } from '../hooks/useClientPagination';
import TablePagination from '../components/TablePagination';

import L from 'leaflet';
import api from '../api';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const Security = () => {
    const { domainId } = useParams();
    const [searchParams] = useSearchParams();
    const domainName = searchParams.get('name') || 'Unknown Domain';
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('sessions');
    const [auditLogs, setAuditLogs] = useState([]);
    const [loginHistory, setLoginHistory] = useState([]);
    const [activeSessions, setActiveSessions] = useState([]);
    const [fimStatus, setFimStatus] = useState([]);
    const [beaconToken, setBeaconToken] = useState('');
    const [loading, setLoading] = useState(true);
    const { success, error } = useToast();

    const sessionsPag = useClientPagination(activeSessions, `sessions-${domainId}`);
    const auditPag = useClientPagination(auditLogs, `audit-${domainId}`);
    const historyPag = useClientPagination(loginHistory, `history-${domainId}`);
    const fimPag = useClientPagination(fimStatus, `fim-${domainId}`);

    const testRateLimit = async () => {
        try {
            await Promise.all(
                Array.from({ length: 50 }).map(() => api.post('/api/security/simulate', { domain_id: domainId }))
            );
            success('Sent 50 requests successfully. Try again to trigger 429!');
            fetchData();
        } catch (err) {
            if (err.response?.status === 429) {
                error('Rate Limit Triggered (429 Too Many Requests)');
            } else {
                error('Test failed');
            }
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'sessions') {
                const res = await api.get(`/api/security/active-sessions?domain_id=${domainId}`);
                setActiveSessions(res.data);
            } else if (activeTab === 'audit') {
                const res = await api.get(`/api/security/audit-logs?domain_id=${domainId}`);
                setAuditLogs(res.data);
            } else if (activeTab === 'history') {
                const res = await api.get(`/api/security/login-history?domain_id=${domainId}`);
                setLoginHistory(res.data);
            } else if (activeTab === 'fim') {
                const res = await api.get(`/api/security/fim-status?domain_id=${domainId}`);
                setFimStatus(res.data);
            } else if (activeTab === 'setup') {
                const res = await api.get(`/api/security/token/${domainId}`);
                setBeaconToken(res.data.token);
            }
        } catch (err) {
            if (err.response?.status === 429) {
                error('Too Many Requests! Slow Down.');
            } else {
                error('Failed to load security data.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRevoke = async (id) => {
        try {
            await api.post('/api/security/revoke-session', { sessionId: id });
            success('Session Revoked Successfully');
            fetchData();
        } catch (err) {
            error('Failed to revoke session');
        }
    };

    return (
        <div className="container mt-4">
            <div className="d-flex align-items-center mb-4">
                <button className="btn btn-outline-secondary btn-sm me-3" onClick={() => navigate(-1)}>
                    <i className="las la-arrow-left me-1"></i>Back
                </button>
                <h2 className="mb-0">
                    <i className="las la-shield-alt me-2 text-warning"></i>
                    Client Security Center: <span className="text-primary">{domainName}</span>
                </h2>
            </div>

            <div className="row mb-4">
                <div className="col-12">
                    <button className="btn btn-dark shadow-sm" onClick={testRateLimit}>
                        <i className="las la-microchip me-2"></i>
                        Simulate Traffic (Generate Fake SaaS Data)
                    </button>
                    <small className="ms-3 text-muted">Click this to synthesize realistic login/WAF data for Investor Demo. Will also trigger Rate Limiting if spammed.</small>
                </div>
            </div>

            <ul className="nav nav-tabs mb-4 border-bottom-0 gap-2">
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-top ${activeTab === 'sessions' ? 'active bg-primary text-white border-primary' : 'bg-light text-dark'}`}
                        onClick={() => setActiveTab('sessions')}
                    >
                        Active Sessions (Zero Trust)
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-top ${activeTab === 'audit' ? 'active bg-primary text-white border-primary' : 'bg-light text-dark'}`}
                        onClick={() => setActiveTab('audit')}
                    >
                        Security Audit (API Protection)
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-top ${activeTab === 'history' ? 'active bg-primary text-white border-primary' : 'bg-light text-dark'}`}
                        onClick={() => setActiveTab('history')}
                    >
                        Login History (Geofencing)
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-top ${activeTab === 'fim' ? 'active bg-primary text-white border-primary' : 'bg-light text-dark'}`}
                        onClick={() => setActiveTab('fim')}
                    >
                        System Health (FIM)
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-top ${activeTab === 'setup' ? 'active bg-dark text-white border-dark' : 'bg-light text-dark'}`}
                        onClick={() => setActiveTab('setup')}
                    >
                        <i className="las la-terminal me-1"></i> API & Webhooks
                    </button>
                </li>
            </ul>

            <div className="card shadow-sm border-0">
                <div className="card-body">
                    {loading ? (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p className="mt-2 text-muted">Loading Security Data...</p>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'sessions' && (
                                <div>
                                    <h5 className="mb-3">Active Device Sessions</h5>
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Device Info</th>
                                                    <th>IP Address</th>
                                                    <th>Created At</th>
                                                    <th>Expires</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sessionsPag.items.length === 0 ? (
                                                    <tr><td colSpan="5" className="text-center text-muted">No active sessions found.</td></tr>
                                                ) : sessionsPag.items.map(sess => (
                                                    <tr key={sess.id}>
                                                        <td><i className="las la-laptop me-2"></i>{sess.device_info || 'Unknown Device'}</td>
                                                        <td>{sess.ip_address}</td>
                                                        <td>{formatDateTime(sess.created_at)}</td>
                                                        <td>{formatDateTime(sess.expires_at)}</td>
                                                        <td>
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleRevoke(sess.id)}>
                                                                Revoke
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <TablePagination {...sessionsPag} onPageChange={sessionsPag.setCurrentPage} />
                                </div>
                            )}

                            {activeTab === 'audit' && (
                                <div>
                                    <h5 className="mb-3 text-danger"><i className="las la-exclamation-triangle me-2"></i>Suspicious Activity Logs</h5>
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle text-sm">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Time</th>
                                                    <th>Type</th>
                                                    <th>IP Address</th>
                                                    <th>Endpoint</th>
                                                    <th>Payload Snippet</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {auditPag.items.length === 0 ? (
                                                    <tr><td colSpan="5" className="text-center text-muted p-4">Clean log! No suspicious API activity detected.</td></tr>
                                                ) : auditPag.items.map(log => (
                                                    <tr key={log.id} className="table-danger">
                                                        <td>{formatDateTime(log.created_at)}</td>
                                                        <td className="fw-bold">{log.event_type}</td>
                                                        <td>{log.ip_address || 'System'}</td>
                                                        <td>{log.endpoint}</td>
                                                        <td><pre className="mb-0 text-dark bg-white p-2 rounded max-h-[100px] overflow-y-auto">{log.payload}</pre></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <TablePagination {...auditPag} onPageChange={auditPag.setCurrentPage} />
                                </div>
                            )}

                            {activeTab === 'history' && (
                                <div>
                                    <h5 className="mb-3">Login Location Tracker</h5>

                                    <div className="rounded overflow-hidden shadow-sm border border-light h-[400px] w-full mb-5">
                                        <MapContainer center={[20, 0]} zoom={2} scrollWheelZoom={false} className="h-[100%] w-[100%]">
                                            <TileLayer
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                            />
                                            {loginHistory.map((log, i) => {
                                                if (!log.latitude || !log.longitude) return null;
                                                return (
                                                    <Marker key={i} position={[log.latitude, log.longitude]}>
                                                        <Popup>
                                                            {log.city}, {log.country} <br />
                                                            IP: {log.ip_address} <br />
                                                            Status: {log.status}
                                                        </Popup>
                                                    </Marker>
                                                );
                                            })}
                                        </MapContainer>
                                    </div>
                                    <p className="text-muted small mb-3"><i className="las la-info-circle me-1"></i> Map requires latitude/longitude points. The table below represents geofencing data saved from IP resolution.</p>

                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Time</th>
                                                    <th>IP Address</th>
                                                    <th>Location</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historyPag.items.length === 0 ? (
                                                    <tr><td colSpan="4" className="text-center text-muted">No login history yet.</td></tr>
                                                ) : historyPag.items.map(log => (
                                                    <tr key={log.id}>
                                                        <td>{formatDateTime(log.created_at)}</td>
                                                        <td>{log.ip_address}</td>
                                                        <td><i className="las la-map-marker text-success me-1"></i>{log.city}, {log.country}</td>
                                                        <td>
                                                            <span className={`badge ${log.status === 'SUCCESS' ? 'bg-success' : 'bg-danger'}`}>
                                                                {log.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <TablePagination {...historyPag} onPageChange={historyPag.setCurrentPage} />
                                </div>
                            )}

                            {activeTab === 'fim' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">File Integrity Monitor (FIM)</h5>
                                        <span className="badge bg-success p-2">Cron Job Active</span>
                                    </div>
                                    <p className="text-muted small mb-4">
                                        Monitoring core backend files (like <code>.env</code>, <code>server.js</code>). If any unauthorized user alters these files on the AWS server, an alert is triggered in the Security Audit log. Compare hashes below against Known Good Hashes.
                                    </p>
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>File Path</th>
                                                    <th>SHA-256 Hash</th>
                                                    <th>Last Checked</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fimPag.items.length === 0 ? (
                                                    <tr><td colSpan="3" className="text-center text-muted">No files scanned yet. Check back in 5 mins.</td></tr>
                                                ) : fimPag.items.map(f => (
                                                    <tr key={f.id}>
                                                        <td className="fw-bold">{f.file_path.split('/').pop()}</td>
                                                        <td><code className="text-dark bg-light px-2 py-1 rounded">{f.file_hash}</code></td>
                                                        <td>{formatDateTime(f.last_checked)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <TablePagination {...fimPag} onPageChange={fimPag.setCurrentPage} />
                                </div>
                            )}

                            {activeTab === 'setup' && (
                                <div className="p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0 text-dark"><i className="las la-code me-2"></i>Developer Integration</h5>
                                        <span className="badge bg-dark p-2 tracking-[0.5px]">Production Webhooks Ready</span>
                                    </div>
                                    <p className="text-muted small mb-4">
                                        To securely push real-time cybersecurity metrics from your live infrastructure (WAF, Authentication Providers, Server Filesystems), POST JSON payloads to our Webhook Receiver using your unique Beacon Token.
                                    </p>

                                    <div className="card border mb-4">
                                        <div className="card-header bg-light fw-bold text-dark">
                                            Your Secure API Configuration
                                        </div>
                                        <div className="card-body bg-white">
                                            <div className="mb-3">
                                                <label className="form-label text-muted small fw-bold">Webhook Endpoint</label>
                                                <div className="input-group">
                                                    <span className="input-group-text"><i className="las la-globe"></i></span>
                                                    <input type="text" className="form-control font-monospace text-primary bg-light" value={`https://secure360.us/api/security/ingest/${beaconToken || '...'}`} readOnly disabled />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="form-label text-muted small fw-bold">Secret Beacon Token</label>
                                                <div className="input-group">
                                                    <span className="input-group-text text-danger bg-danger bg-opacity-10"><i className="las la-key"></i></span>
                                                    <input type="text" className="form-control font-monospace border-danger border-opacity-50 text-danger fw-bold" value={beaconToken || '...'} readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <h6 className="fw-bold mt-4"><i className="lab la-node-js me-1"></i> Example: Pushing a WAF Payload Arrest (Node.js/cURL)</h6>
                                    <pre className="bg-dark text-light p-4 rounded shadow-sm text-sm overflow-x-auto border-l-4 border-l-[#198754]">
                                        {`curl -X POST "https://secure360.us/api/security/ingest/${beaconToken || 'YOUR_TOKEN'}" \\
-H "Content-Type: application/json" \\
-d '{
    "type": "WAF_EVENT",
    "payload": {
        "ip_address": "8.8.8.8",
        "endpoint": "/v1/auth/verify",
        "details": "BLOCKED: Exploit [SQLi] signature matched in param [username_input]. Connection dropped."
    }
}'`}
                                    </pre>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Security;
